import React, { useEffect, useId, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Pagination } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  AI_RISK_REPORT_PDF_KEEP_WITH_NEXT_ATTRIBUTES,
  AI_RISK_REPORT_PDF_PAGE_BREAK_PROTECTED_ATTRIBUTES,
} from '../../data/constants'
import { AI_RISK_REPORT_CHANNELS } from '../../data/reportChannel'
import { useAIRiskReportChannel, useAIRiskReportExportMode } from '../RenderMode/AIRiskReportRenderModeProvider'
import styles from './ReportTable.module.scss'

const BETA_BROWSER_PAGE_SIZE = 10
const DEFAULT_BETA_EXPORT_ROW_LIMIT = 10

const normalizeSearchValue = (value) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase()

const getColumnSearchValues = (column, row) => {
  if (column.searchable === false) return []

  const value = column.searchValue ? column.searchValue(row) : row[column.key]
  const values = Array.isArray(value) ? value : [value]
  return values.filter((entry) => ['string', 'number', 'boolean'].includes(typeof entry))
}

export default function ReportTable({
  columns,
  rows,
  totalRows = rows.length,
  empty = null,
  footer = null,
  exportRowLimit = DEFAULT_BETA_EXPORT_ROW_LIMIT,
  exportTableClassName = '',
  affectedEntityLabelId,
  isAffectedEntityTable = true,
}) {
  const [sort, setSort] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const searchInputId = useId()
  const intl = useIntl()
  const isExportMode = useAIRiskReportExportMode()
  const channel = useAIRiskReportChannel()
  const isBeta = channel === AI_RISK_REPORT_CHANNELS.BETA
  const isBetaBrowserMode = isBeta && !isExportMode
  const isBetaExportMode = isBeta && isExportMode

  const normalizedSearchText = normalizeSearchValue(searchText)
  const filteredRows = useMemo(() => {
    if (!isBetaBrowserMode || !normalizedSearchText) return rows

    return rows.filter((row) =>
      columns.some((column) =>
        getColumnSearchValues(column, row).some((value) => normalizeSearchValue(value).includes(normalizedSearchText)),
      ),
    )
  }, [columns, isBetaBrowserMode, normalizedSearchText, rows])

  const sortedRows = useMemo(() => {
    if (isExportMode || !sort) return filteredRows
    const column = columns.find((entry) => entry.key === sort.key)
    if (!column) return filteredRows
    const getSortValue = column.sortValue || ((row) => row[column.key])
    const direction = sort.direction === 'desc' ? -1 : 1
    return [...filteredRows].sort((first, second) => {
      const firstValue = getSortValue(first)
      const secondValue = getSortValue(second)
      if (firstValue == null && secondValue == null) return 0
      if (firstValue == null) return 1 * direction
      if (secondValue == null) return -1 * direction
      if (typeof firstValue === 'number' && typeof secondValue === 'number')
        return (firstValue - secondValue) * direction
      return String(firstValue).localeCompare(String(secondValue)) * direction
    })
  }, [columns, filteredRows, isExportMode, sort])

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / BETA_BROWSER_PAGE_SIZE))
  const displayedPage = Math.min(currentPage, pageCount)
  const isPaginated = rows.length > BETA_BROWSER_PAGE_SIZE

  useEffect(() => {
    if (isBetaBrowserMode) setCurrentPage(1)
  }, [isBetaBrowserMode, rows])

  const displayedRows = useMemo(() => {
    if (isBetaExportMode) return sortedRows.slice(0, exportRowLimit)
    if (!isBetaBrowserMode) return sortedRows

    const pageStart = (displayedPage - 1) * BETA_BROWSER_PAGE_SIZE
    return sortedRows.slice(pageStart, pageStart + BETA_BROWSER_PAGE_SIZE)
  }, [displayedPage, exportRowLimit, isBetaBrowserMode, isBetaExportMode, sortedRows])

  const displayedStart = (displayedPage - 1) * BETA_BROWSER_PAGE_SIZE + 1
  const displayedEnd = Math.min(displayedPage * BETA_BROWSER_PAGE_SIZE, sortedRows.length)
  const isServerTruncated = totalRows > sortedRows.length
  const isSearchActive = Boolean(normalizedSearchText)
  const hasSearchResults = sortedRows.length > 0
  const isBetaExportTruncated = isBetaExportMode && (displayedRows.length < sortedRows.length || isServerTruncated)

  const toggleSort = (key) => {
    if (isBetaBrowserMode) setCurrentPage(1)
    setSort((previousSort) => {
      if (!previousSort || previousSort.key !== key) return { key, direction: 'asc' }
      if (previousSort.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getSortArrow = (column) => {
    if (sort?.key !== column.key) return '⇅'
    return sort.direction === 'asc' ? '▲' : '▼'
  }

  const getAriaSort = (column) => {
    if (isExportMode) return undefined
    if (sort?.key !== column.key) return undefined
    return sort.direction === 'asc' ? 'ascending' : 'descending'
  }

  const getColumnClassName = (column) =>
    [column.className, column.sortable && !isExportMode ? styles.headerSortable : ''].filter(Boolean).join(' ') ||
    undefined

  if (rows.length === 0) return empty

  const wrapClassName = [styles.wrap, isExportMode ? styles.exportWrap : ''].filter(Boolean).join(' ')
  let paginationMessageId = 'AI_RISK_REPORT.TABLE.PAGINATION_TOTAL_SUMMARY'
  if (isSearchActive && totalRows > rows.length)
    paginationMessageId = 'AI_RISK_REPORT.TABLE.SEARCH_TRUNCATED_RESULTS_SUMMARY'
  else if (isSearchActive) paginationMessageId = 'AI_RISK_REPORT.TABLE.SEARCH_RESULTS_SUMMARY'
  else if (isServerTruncated) paginationMessageId = 'AI_RISK_REPORT.TABLE.PAGINATION_TRUNCATED_SUMMARY'
  else if (isAffectedEntityTable) paginationMessageId = 'AI_RISK_REPORT.TABLE.PAGINATION_SUMMARY'

  const betaFooter =
    isBetaBrowserMode && (isPaginated || isServerTruncated) ? (
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
        {hasSearchResults && (
          <span>
            <FormattedMessage
              id={paginationMessageId}
              values={{
                total: isServerTruncated ? totalRows : sortedRows.length,
                returned: rows.length,
                matches: sortedRows.length,
                start: displayedStart,
                end: displayedEnd,
                entity: intl.formatMessage({ id: affectedEntityLabelId }),
              }}
            />
          </span>
        )}
        {isPaginated && (
          <div className={`${styles.betaFooterControls} d-flex align-items-center ms-sm-auto`}>
            <Form.Group className={styles.search} controlId={searchInputId}>
              <Form.Label className="visually-hidden">
                <FormattedMessage
                  id="AI_RISK_REPORT.TABLE.SEARCH_LABEL"
                  values={{ entity: intl.formatMessage({ id: affectedEntityLabelId }) }}
                />
              </Form.Label>
              <Form.Control
                type="search"
                value={searchText}
                placeholder={intl.formatMessage(
                  { id: 'AI_RISK_REPORT.TABLE.SEARCH_PLACEHOLDER' },
                  { entity: intl.formatMessage({ id: affectedEntityLabelId }) },
                )}
                onChange={(event) => {
                  setSearchText(event.target.value)
                  setCurrentPage(1)
                }}
              />
            </Form.Group>
            {pageCount > 1 && (
              <Pagination className="flex-shrink-0 flex-nowrap mb-0" size="sm">
                <Pagination.Prev
                  linkClassName={`${styles.paginationLink} d-flex align-items-center justify-content-center p-0 lh-1`}
                  disabled={displayedPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  aria-label={intl.formatMessage({ id: 'AI_RISK_REPORT.TABLE.PREVIOUS_PAGE' })}
                >
                  <i className="fas fa-chevron-left" aria-hidden="true" />
                </Pagination.Prev>
                <Pagination.Next
                  linkClassName={`${styles.paginationLink} d-flex align-items-center justify-content-center p-0 lh-1`}
                  disabled={displayedPage === pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  aria-label={intl.formatMessage({ id: 'AI_RISK_REPORT.TABLE.NEXT_PAGE' })}
                >
                  <i className="fas fa-chevron-right" aria-hidden="true" />
                </Pagination.Next>
              </Pagination>
            )}
          </div>
        )}
      </div>
    ) : null

  const betaExportFooter = isBetaExportTruncated ? (
    <FormattedMessage
      id="AI_RISK_REPORT.TABLE.EXPORT_TRUNCATION_SUMMARY"
      values={{
        shown: displayedRows.length,
        total: totalRows,
        entity: intl.formatMessage({ id: affectedEntityLabelId }),
      }}
    />
  ) : null

  return (
    <div className={wrapClassName}>
      {isBetaBrowserMode && isSearchActive && !hasSearchResults ? (
        <div className="py-4 px-3 text-body-secondary text-center">
          <FormattedMessage
            id="AI_RISK_REPORT.TABLE.NO_SEARCH_RESULTS"
            values={{ entity: intl.formatMessage({ id: affectedEntityLabelId }) }}
          />
        </div>
      ) : (
        <table className={[styles.table, 'w-100', isExportMode ? exportTableClassName : ''].filter(Boolean).join(' ')}>
          <thead {...AI_RISK_REPORT_PDF_KEEP_WITH_NEXT_ATTRIBUTES}>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={getColumnClassName(column)} aria-sort={getAriaSort(column)}>
                  {column.sortable && !isExportMode ? (
                    <button type="button" className={styles.sortButton} onClick={() => toggleSort(column.key)}>
                      {column.header}
                      <span
                        className={`${styles.sortIndicator} ${sort?.key === column.key ? styles.sortIndicatorActive : ''}`}
                        aria-hidden="true"
                      >
                        {getSortArrow(column)}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row) => (
              <tr key={row.key}>
                {columns.map((column) => (
                  <td key={column.key} className={column.className}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {isBeta && (betaFooter || betaExportFooter) && (
        <div
          className={`${styles.footer} ${isBetaBrowserMode && isPaginated ? styles.footerWithSearch : ''}`}
          {...AI_RISK_REPORT_PDF_PAGE_BREAK_PROTECTED_ATTRIBUTES}
        >
          {betaFooter || betaExportFooter}
        </div>
      )}
      {!isBeta && footer && (
        <div className={styles.footer} {...AI_RISK_REPORT_PDF_PAGE_BREAK_PROTECTED_ATTRIBUTES}>
          {footer}
        </div>
      )}
    </div>
  )
}

ReportTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.node.isRequired,
      render: PropTypes.func.isRequired,
      sortable: PropTypes.bool,
      sortValue: PropTypes.func,
      searchable: PropTypes.bool,
      searchValue: PropTypes.func,
      className: PropTypes.string,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired })).isRequired,
  totalRows: PropTypes.number,
  empty: PropTypes.node,
  footer: PropTypes.node,
  exportRowLimit: PropTypes.number,
  exportTableClassName: PropTypes.string,
  affectedEntityLabelId: PropTypes.string.isRequired,
  isAffectedEntityTable: PropTypes.bool,
}
